import React, { useEffect, useRef } from "react"
import { Pressable } from "react-native"

import { type UITutorReadyProposal } from "../../../ports/primary/ui/types/tutorLive"
import { Icon } from "../../atoms/icons/Icon"

const TEXTS = {
    peek: (title: string) => `${title}, la voir au centre`,
}

/**
 * Une proposition qu'on survole et qu'on touche, dans la carte « prêtes » et dans l'onglet « Fil » : survolée prête, son
 * aperçu au centre (`onPeek`) ; touchée, sa relecture (`onPress`) ; l'œil marque celle qu'on regarde. Chacun pose sa
 * typographie (`children`) et son style de rangée.
 *
 * L'aperçu appartient au survol : une rangée qui se démonte sous la souris (la carte qui quitte « À traiter » quand un
 * ajout décide la demande) le referme, puisqu'aucune « fin de survol » n'arrivera plus (Wajih, 05/11 : il restait
 * bloqué au centre, par-dessus le Profil).
 */
export function ProposalPeekRow(props: {
    proposal: UITutorReadyProposal
    peekId?: string | null
    onPeek?: (id: string | null) => void
    onPress?: () => void
    style: React.ComponentProps<typeof Pressable>["style"]
    children: React.ReactNode
}) {
    const { proposal, peekId, onPeek, onPress, style, children } = props
    const hovered = useRef(false)
    const onPeekRef = useRef(onPeek)
    onPeekRef.current = onPeek
    useEffect(
        () => () => {
            if (hovered.current) onPeekRef.current?.(null)
        },
        []
    )
    return (
        <Pressable
            onHoverIn={
                proposal.status === "ready"
                    ? () => {
                          hovered.current = true
                          onPeek?.(proposal.id)
                      }
                    : undefined
            }
            onHoverOut={() => {
                hovered.current = false
                onPeek?.(null)
            }}
            onPress={onPress}
            disabled={onPress == null}
            accessibilityRole="button"
            accessibilityLabel={TEXTS.peek(proposal.title)}
            style={style}
        >
            {children}
            {proposal.id === peekId && <Icon name="eye" size={12} color="role" />}
        </Pressable>
    )
}
